import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

import Icon from "@/components/Icon/Icon";
import { type PlaceOption, PlaceSelect } from "@/components/Select/PlaceSelect";
import { LabelPlace } from "@/features/place/components/LabelPlace/LabelPlace";
import { apiClient } from "@/lib/api/client";

import {
  CFormItemBase,
  type CFormItemProps,
  buildCFormItem,
} from "../CFormItem";

type Props = CFormItemProps & {
  value?: string;
  allowEmpty?: boolean;
  readComponent?: ReactNode;
  onChange: (placeId?: string) => void;
  inputWidth?: number | string;
  inputClassName?: string;
  showPlaceLink?: boolean;
};

/**
 * Card-based Form / PlaceItem
 */
export const CFormPlaceSelectItem = buildCFormItem<Props>((props: Props) => {
  const { data: selectedPlace } = useQuery({
    queryKey: ["placeData", props.value],
    queryFn: async () => {
      if (!props.value) return null;

      const placeRes = await apiClient.GET("/v2/places/{placeId}", {
        params: { path: { placeId: props.value } },
      });

      return placeRes.data;
    },
    enabled: !!props.value,
  });

  const handlePlaceChange = (place?: PlaceOption) => {
    props.onChange(place?.id);
  };

  return (
    <CFormItemBase
      {...props}
      readComponent={
        props.readComponent ??
        (props.value && selectedPlace ? (
          <div className="flex items-center gap-2">
            {props.showPlaceLink && selectedPlace.id && (
              <Link
                to="/places/$id"
                target="_blank"
                rel="noopener noreferrer"
                params={{ id: selectedPlace.id }}
              >
                <Icon iconName="SquareArrowOutUpRightIcon" />
              </Link>
            )}
            <LabelPlace
              name={selectedPlace.name}
              avatarUrl={selectedPlace.avatarUrl}
            />
          </div>
        ) : (
          <></>
        ))
      }
      writeComponent={
        <PlaceSelect
          disabled={props.readonly}
          allowEmpty={props.allowEmpty}
          value={props.value}
          onChange={handlePlaceChange}
          width={
            typeof props.inputWidth === "number" ? props.inputWidth : undefined
          }
          className={props.inputClassName}
        />
      }
    />
  );
});
